/**
 * ICA and sessional workforce.
 *
 * The framing IS the panel. Independent contractors and casual staff are
 * ENGAGED WORKERS, NOT EMPLOYEES: they sit outside every employee figure on this
 * page, they combine with employees only in FTE-based cost and productivity
 * measures, and an engagement count is not a headcount — contractors appear on
 * the payroll only in the months they actually work, and most work part-time. So
 * the panel says all three things on its face, and states which of the numbers
 * above these people are inside on the basis currently selected.
 *
 * Two sections, two dates, each named on its own. The profile is point-in-time
 * on the worker records: who holds a live ICA or casual engagement, and who they
 * are. "Paid in the month" follows the cohort into a closed Finance payroll
 * month: engaged on the month's last day, contractors paid, the full-time
 * equivalents that pay represents, students per lecturer FTE. The two engaged
 * counts are taken on different days, so they are never read as a movement.
 *
 * NULL MEANS WITHHELD; A MISSING KEY MEANS NOT AVAILABLE FOR THIS DATE. The paid
 * bridge's counts are withheld together, and the page never works one out from
 * the engaged and paid totals printed beside it. Neither section follows the
 * period picker or the basis toggle — the same people are engaged whichever basis
 * is selected; only their relationship to the figures above changes, which is
 * exactly what the callout under the heading states.
 */
import React, { useMemo, type ReactNode } from 'react';
import { MetricTile, TileRow } from './MetricTile';
import { PanelShell, PanelSkeleton, PanelUnavailable } from './PanelShell';
import {
  CATEGORY_COLORS,
  Def,
  fmtDay,
  fmtNum,
  fmtOne,
  fmtPct,
  panelDateLabel,
  readExtras,
  type DefLookup,
  type SessionalCapacity,
  type WorkforceBasis,
  type WorkforcePanels,
} from './apiContract';

interface Props {
  panels: WorkforcePanels | null;
  basis: WorkforceBasis;
  loading: boolean;
  def: DefLookup;
}

/** A served figure: a number, null (withheld), or undefined (not carried for this date). */
type Cell = number | null | undefined;

const ICA_COLOR = CATEGORY_COLORS.ICA; // #0B7F72
const CASUAL_COLOR = CATEGORY_COLORS.Casual; // #CFAD80

const WITHHELD = 'withheld to protect small groups';
const NOT_AVAILABLE = 'not available for this date';

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const years = (n: number) => `${fmtOne(n)} yrs`;

/** A count identity withholds only below five, in the words of a sentence. */
const smallCount = (v: Cell) =>
  v === undefined ? NOT_AVAILABLE : v === null ? 'fewer than five' : fmtNum(v);

// =============================================================================
// DEFINITIONS — server definitions win; these fill the keys /metric-definitions
// does not publish yet, so no label on this panel is left without one.
// =============================================================================

const defBody = (label: string, body: string, caveat?: string): ReactNode => (
  <div className="space-y-1.5">
    <p className="font-semibold text-white">{label}</p>
    <p>{body}</p>
    {caveat ? (
      <ul className="list-disc space-y-0.5 pl-3.5 text-[11px] text-amber-200/90">
        <li>{caveat}</li>
      </ul>
    ) : null}
  </div>
);

const FALLBACK_DEFS: Record<string, ReactNode> = {
  sessional_engagements: defBody(
    'Active engagements',
    'People whose most permanent position on the date shown is an independent-contractor or casual engagement.',
    'An engagement count, not a headcount and not capacity — these people are not employees and are never added to the employee figures.',
  ),
  sessional_female_pct: defBody(
    'Women',
    'Women as a percentage of the people holding an active sessional engagement.',
  ),
  sessional_african_pct: defBody(
    'African',
    'People recorded as African as a percentage of the sessional cohort.',
    'Sessional workers are not employees, so they do not form part of the employment-equity employee complement.',
  ),
  sessional_median_age: defBody('Median age', 'The middle age in the sessional cohort.'),
  sessional_median_service: defBody(
    'Median service',
    'Time since the first position assignment recorded in the HR system, in years.',
    'Not continuous service — sessional work is intermittent by design and breaks are not deducted.',
  ),
  sessional_sixty_plus: defBody(
    'Aged 60 or over',
    'People aged 60 or over holding an active sessional engagement.',
    'No retirement age applies: these are contracts for services, not employment.',
  ),
  sessional_foreign_nationals: defBody(
    'Foreign nationals',
    'People in the sessional cohort whose recorded nationality is not South African.',
  ),
  sessional_capacity: defBody(
    'Paid in the month',
    "Follows the sessional workforce from engagement to pay: people engaged on the payroll month's last day, the contractors Finance paid that month, the full-time equivalents that pay represents, and students per lecturer full-time equivalent.",
    "Finance's payroll has no casual type, so casual workers cannot be followed into pay.",
  ),
  sessional_contractors_paid: defBody(
    'Contractors paid',
    'Distinct independent contractors Finance paid in the payroll month.',
    'Contractors are paid only in the months they work, so fewer are paid than engaged.',
  ),
  sessional_paid_fte: defBody(
    'Full-time equivalents',
    "The full-time equivalents on the month's contractor payroll lines: each contractor counts as the share of a full-time equivalent their month's cost represents, capped at one.",
  ),
  small_cell_suppression: defBody(
    'Withheld to protect small groups',
    'Fewer than five people, or a figure that would let such a group be worked out, so the count is not shown. The people are still inside the totals.',
  ),
};

/** Server definition first, local wording only where the server has none. */
function extendDefs(def: DefLookup, extra: Record<string, ReactNode>): DefLookup {
  return (key: string) => def(key) ?? extra[key];
}

/**
 * A served figure. A withheld value is NOT a zero: it reads "withheld to protect
 * small groups" with the suppression definition, never 0 and never dropped into a
 * sum. A key the response does not carry reads "not available for this date".
 */
const Figure: React.FC<{ v: Cell; def: DefLookup; fmt?: (n: number) => string; tile?: boolean }> = ({
  v,
  def,
  fmt = fmtNum,
  tile,
}) => {
  if (typeof v === 'number') return <>{fmt(v)}</>;
  const words = v === null ? WITHHELD : NOT_AVAILABLE;
  return (
    <span
      className={
        tile
          ? 'inline-flex items-center text-[12px] font-medium leading-snug text-gray-400'
          : 'inline-flex items-center text-[11px] font-normal text-gray-500'
      }
    >
      {tile ? capitalise(words) : words}
      {v === null ? <Def def={def} k="small_cell_suppression" /> : null}
    </span>
  );
};

/** ICA / casual proportion at a glance. */
const SplitStrip: React.FC<{ ica: number; casual: number; total: number }> = ({
  ica,
  casual,
  total,
}) =>
  total > 0 ? (
    <div className="flex h-[13px] w-full overflow-hidden rounded-sm bg-gray-100">
      {ica > 0 ? (
        <span
          title={`ICA: ${fmtNum(ica)} (${fmtPct((ica / total) * 100)})`}
          style={{ width: `${(ica / total) * 100}%`, minWidth: 3, background: ICA_COLOR }}
        />
      ) : null}
      {casual > 0 ? (
        <span
          title={`Casual: ${fmtNum(casual)} (${fmtPct((casual / total) * 100)})`}
          style={{ width: `${(casual / total) * 100}%`, minWidth: 3, background: CASUAL_COLOR }}
        />
      ) : null}
    </div>
  ) : null;

const BASIS_NOTE: Record<WorkforceBasis, string> = {
  employees:
    'None of these people are in the employee figures above — the employee basis is Permanent and fixed-term only.',
  engaged:
    'These people ARE inside the engaged-workforce figures above. They stay outside every employee figure on this page.',
  positions:
    'The positions they hold are inside the approved-position figures above; the people are not employees.',
};

/** The finalised board pack's slide-37 figures this reading moves: bridged, never restated. */
function packBridgeLine(cap: SessionalCapacity): string | null {
  const entries = cap.published ?? [];
  const entry =
    entries.find((e) => e.tab === 'M34' && /^finalised/i.test(e.pack)) ??
    entries.find((e) => e.tab === 'M34');
  if (!entry || !entry.now_differs?.length) return null;
  const figures: string[] = [];
  if (typeof entry.published?.engaged_total === 'number') {
    figures.push(`${fmtNum(entry.published.engaged_total)} engaged`);
  }
  if (typeof entry.published?.students_per_lecturer_fte === 'number') {
    figures.push(`${fmtOne(entry.published.students_per_lecturer_fte)} students per lecturer FTE`);
  }
  return [
    figures.length ? `The board pack showed ${figures.join(' and ')}.` : null,
    entry.now_cause,
    entry.re_read_differs?.length ? entry.re_read_cause : null,
  ]
    .filter(Boolean)
    .join(' ');
}

// =============================================================================
// PAID IN THE MONTH — the payroll half of the slide, dated by its payroll month
// =============================================================================

const CapacitySection: React.FC<{
  cap: SessionalCapacity | null;
  dateLabel: string;
  hasProfile: boolean;
  def: DefLookup;
}> = ({ cap, dateLabel, hasProfile, def }) => {
  const heading = (
    <div className="mb-1.5 flex flex-wrap items-center gap-x-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
      Paid in the month
      <Def def={def} k="sessional_capacity" />
      {cap ? (
        <span className="font-normal normal-case tracking-normal text-gray-500">· {dateLabel}</span>
      ) : null}
    </div>
  );
  if (!cap) {
    return (
      <div className="border-t border-gray-100 pt-3">
        {heading}
        <div className="text-[11px] leading-snug text-gray-500">Not available for this date.</div>
      </div>
    );
  }

  const bridge = cap.bridge;
  const engagedIca: Cell = cap.engaged?.ICA;
  const paidOfEngaged: Cell = bridge?.engaged_contractors_paid;
  const paidNotEngaged: Cell = bridge?.paid_contractors_not_engaged_as_contractor;
  const bridgeLine = packBridgeLine(cap);

  return (
    <div className="border-t border-gray-100 pt-3">
      {heading}
      <TileRow min="130px">
        <MetricTile
          label={`Engaged at ${fmtDay(cap.engaged_as_at)}`}
          value={<Figure v={cap.engaged?.total} def={def} tile />}
          sub={
            <>
              ICA <Figure v={engagedIca} def={def} /> · Casual{' '}
              <Figure v={cap.engaged?.Casual} def={def} />
            </>
          }
          defKey="sessional_engagements"
          def={def}
          size="sm"
        />
        <MetricTile
          label="Contractors paid"
          value={<Figure v={cap.paid?.contractors} def={def} tile />}
          sub="distinct people paid in the month"
          defKey="sessional_contractors_paid"
          def={def}
          size="sm"
        />
        <MetricTile
          label="Full-time equivalents"
          value={<Figure v={cap.paid?.fte} fmt={fmtOne} def={def} tile />}
          sub="capped at one per person"
          defKey="sessional_paid_fte"
          def={def}
          size="sm"
        />
        <MetricTile
          label="Students per lecturer FTE"
          value={<Figure v={cap.students_per_lecturer_fte} fmt={fmtOne} def={def} tile />}
          sub="every lecturer paid, employees and contractors"
          defKey="students_per_lecturer_fte"
          def={def}
          size="sm"
        />
      </TileRow>

      <p className="mt-2 text-[11.5px] leading-snug text-gray-600">
        {typeof paidOfEngaged === 'number' ? (
          <>
            <b>{fmtNum(paidOfEngaged)}</b> of the {fmtNum(engagedIca)} contractors engaged at month
            end were paid in the month.
          </>
        ) : (
          <>
            How many of the {fmtNum(engagedIca)} contractors engaged at month end were paid in the
            month is <Figure v={paidOfEngaged} def={def} />.
          </>
        )}{' '}
        {typeof paidNotEngaged === 'number' ? (
          <>
            <b>{fmtNum(paidNotEngaged)}</b> contractors paid that month were not engaged as
            contractors at month end.
          </>
        ) : (
          <>
            How many contractors paid that month were not engaged as contractors at month end is{' '}
            <Figure v={paidNotEngaged} def={def} />.
          </>
        )}
      </p>
      <p className="mt-1 text-[11px] leading-snug text-gray-500">
        Finance&apos;s payroll has no casual type, so casual workers cannot be followed into pay.
        {hasProfile
          ? " Engaged here is counted on the payroll month's last day, so it is not the same count as the active engagements above."
          : null}
        {bridgeLine ? ` ${bridgeLine}` : null}
      </p>
    </div>
  );
};

// =============================================================================
// PANEL
// =============================================================================

export const SessionalPanel: React.FC<Props> = ({ panels, basis, loading, def }) => {
  const d = useMemo(() => extendDefs(def, FALLBACK_DEFS), [def]);
  const profile = readExtras(panels, 'sessional_profile');
  const capacity = readExtras(panels, 'sessional_capacity');
  const count: Cell = profile?.count;
  const ica: Cell = profile?.split?.ICA;
  const casual: Cell = profile?.split?.Casual;

  const demographics: { label: string; value: ReactNode; sub?: string; defKey: string }[] = profile
    ? [
        {
          label: 'Women',
          value: <Figure v={profile.female_pct} fmt={fmtPct} def={d} />,
          sub: typeof profile.female === 'number' ? `${fmtNum(profile.female)} people` : undefined,
          defKey: 'sessional_female_pct',
        },
        {
          label: 'African',
          value: <Figure v={profile.african_pct} fmt={fmtPct} def={d} />,
          sub: typeof profile.african === 'number' ? `${fmtNum(profile.african)} people` : undefined,
          defKey: 'sessional_african_pct',
        },
        {
          label: 'Median age',
          value: <Figure v={profile.median_age} fmt={years} def={d} />,
          defKey: 'sessional_median_age',
        },
        {
          label: 'Median service',
          value: <Figure v={profile.median_service} fmt={years} def={d} />,
          sub: typeof profile.median_service === 'number' ? 'since first recorded assignment' : undefined,
          defKey: 'sessional_median_service',
        },
        {
          label: 'Aged 60 or over',
          value: <Figure v={profile.sixty_plus} def={d} />,
          defKey: 'sessional_sixty_plus',
        },
        {
          label: 'Foreign nationals',
          value: <Figure v={profile.foreign_nationals} def={d} />,
          defKey: 'sessional_foreign_nationals',
        },
      ]
    : [];

  return (
    <PanelShell
      title="ICA and sessional workforce"
      defKey="sessional"
      def={d}
      window={
        <>
          People whose most permanent position is an independent-contractor or casual engagement —
          engaged workers, not employees.
          {panels ? (
            <>
              {' '}
              Profile: {profile ? panelDateLabel(panels, 'sessional_profile') : NOT_AVAILABLE}. Paid in
              the month: {capacity ? panelDateLabel(panels, 'sessional_capacity') : NOT_AVAILABLE}.
            </>
          ) : null}{' '}
          Independent of the basis toggle, the period picker and the range slicer.
        </>
      }
      footnote={
        <>
          <b>An engagement count is not a headcount, and not a capacity figure.</b> Contractors
          appear on the payroll only in the months they actually work, and most work part-time, so
          engagements overstate month-to-month capacity — cost and productivity measures use
          full-time equivalents instead, capped at one per person (the paid headcount and
          full-time-equivalent panel counts the people paid in a closed payroll month, so it
          describes an earlier date than the profile). These people are never added to the employee
          headcount.
          {profile ? (
            <>
              {' '}
              Not recorded on the worker record: gender {smallCount(profile.gender_unknown)}, race{' '}
              {smallCount(profile.race_unknown)}, birth date {smallCount(profile.age_not_captured)},
              nationality {smallCount(profile.nationality_not_captured)}. Shares are of everyone
              engaged, so these people stay in the denominator.
            </>
          ) : null}
        </>
      }
    >
      {loading && !panels ? (
        <PanelSkeleton height={240} />
      ) : !panels ? (
        <PanelUnavailable what="The sessional workforce view" />
      ) : (
        <div className="space-y-4">
          <div className="rounded border border-teal-200 bg-teal-50/70 p-2.5 text-[11px] leading-snug text-teal-900">
            <b>Sessional staff are engaged workers, not employees.</b> {BASIS_NOTE[basis]}
          </div>

          {profile ? (
            <>
              <TileRow min="150px">
                <MetricTile
                  label="Active engagements"
                  value={<Figure v={count} def={d} tile />}
                  sub="people holding a live sessional engagement"
                  defKey="sessional_engagements"
                  def={d}
                  size="sm"
                />
                <MetricTile
                  label="ICA"
                  value={<Figure v={ica} def={d} tile />}
                  sub={
                    typeof ica === 'number' && typeof count === 'number' && count > 0
                      ? `${fmtPct((ica / count) * 100)} of engagements`
                      : 'independent contractor agreements'
                  }
                  defKey="ICA"
                  def={d}
                  size="sm"
                />
                <MetricTile
                  label="Casual"
                  value={<Figure v={casual} def={d} tile />}
                  sub={
                    typeof casual === 'number' && typeof count === 'number' && count > 0
                      ? `${fmtPct((casual / count) * 100)} of engagements`
                      : 'invigilation and marking pools'
                  }
                  defKey="Casual"
                  def={d}
                  size="sm"
                />
              </TileRow>

              {typeof ica === 'number' && typeof casual === 'number' && typeof count === 'number' ? (
                <div>
                  <SplitStrip ica={ica} casual={casual} total={count} />
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-600">
                    <span className="inline-flex items-center gap-1.5">
                      <span
                        className="inline-block h-2.5 w-2.5 rounded-sm"
                        style={{ background: ICA_COLOR }}
                      />
                      ICA · {fmtNum(ica)}
                      <Def def={d} k="ICA" />
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span
                        className="inline-block h-2.5 w-2.5 rounded-sm"
                        style={{ background: CASUAL_COLOR }}
                      />
                      Casual · {fmtNum(casual)}
                      <Def def={d} k="Casual" />
                    </span>
                  </div>
                </div>
              ) : null}

              <div>
                <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                  Who the sessional workforce is
                </div>
                <div className="grid gap-x-4 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
                  {demographics.map((item) => (
                    <div
                      key={item.label}
                      className="flex items-baseline justify-between gap-2 border-b border-gray-100 pb-1"
                    >
                      <span className="flex min-w-0 items-center text-[11.5px] text-gray-600">
                        <span className="truncate">{item.label}</span>
                        <Def def={d} k={item.defKey} />
                      </span>
                      <span className="shrink-0 text-right">
                        <span className="text-[13px] font-semibold tabular-nums text-heritage-blue">
                          {item.value}
                        </span>
                        {item.sub ? (
                          <span className="ml-1 text-[10px] text-gray-400">{item.sub}</span>
                        ) : null}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="rounded border border-gray-200 bg-gray-50 p-2.5 text-[11px] leading-snug text-gray-600">
              The sessional profile is not available for this date.
            </div>
          )}

          <CapacitySection
            cap={capacity}
            dateLabel={capacity ? panelDateLabel(panels, 'sessional_capacity') : ''}
            hasProfile={!!profile}
            def={d}
          />
        </div>
      )}
    </PanelShell>
  );
};
